import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  EMBED_PARENT_VIEWPORT_TYPE,
  IFRAME_EMBED_REQUEST_PARENT_VIEWPORT_TYPE,
} from './embed-parent-viewport.contract';
import { isEmbeddedFromParentOrigin } from './iframe-embed.util';

/**
 * When the SPA is embedded in a tall iframe (parent sets iframe height from document height),
 * Material dialogs and ngx-toastr use %/vh positioning relative to the full iframe document,
 * so they can appear outside the visible browser band. This service pins overlays to
 * `window.visualViewport` via CSS variables on the document root (see `styles.scss`).
 *
 * It also positions `.cdk-overlay-pane` elements that contain `.mat-mdc-dialog-container`
 * (Angular Material 21 / MDC): centered in the parent-visible band when the parent posts
 * it, otherwise bottom-aligned near the opening button as a fallback.
 */
@Injectable({
  providedIn: 'root',
})
export class EmbeddedParentViewportService implements OnDestroy {
  private static readonly EMBEDDED_CLASS = 'embedded-from-parent';
  private static readonly DIALOG_PATCH_ATTR = 'data-embedded-dialog-viewport-patch';
  /** Angular Material 21 (MDC) dialog container. */
  private static readonly MAT_DIALOG_CONTAINER_SELECTOR =
    '.mat-mdc-dialog-container';
  /** Gap from the bottom of the visible band (or above the trigger button). */
  private static readonly BOTTOM_GAP_PX = 25;
  private static readonly CSS_VARS = [
    '--embedded-vv-top',
    '--embedded-vv-left',
    '--embedded-vv-width',
    '--embedded-vv-height',
  ] as const;

  private started = false;
  private rafId?: number;
  private mutationObserver?: MutationObserver;
  /** Band of the iframe visible inside the parent (CSS px); set via parent postMessage. */
  private parentViewportOverride: {
    top: number;
    left: number;
    width: number;
    height: number;
  } | null = null;
  private lastParentViewportPingAt = 0;
  /** Last button the user clicked, used to anchor a dialog when there is no parent viewport band. */
  private lastClickedButton: HTMLElement | null = null;

  private readonly onDocumentClick = (ev: MouseEvent): void => {
    const target = ev.target as HTMLElement | null;
    const btn = target?.closest('button') as HTMLElement | null;
    if (btn) {
      this.lastClickedButton = btn;
    }
  };

  private readonly onParentViewportMessage = (ev: MessageEvent): void => {
    if (ev.source != window.parent) {
      return;
    }
    const d = ev.data as {
      type?: string;
      top?: unknown;
      left?: unknown;
      width?: unknown;
      height?: unknown;
    };
    if (!d || d.type !== EMBED_PARENT_VIEWPORT_TYPE) {
      return;
    }
    const top = Number(d.top);
    const left = Number(d.left);
    const width = Number(d.width);
    const height = Number(d.height);
    if (!isFinite(width) || !isFinite(height) || width <= 0 || height <= 0) {
      this.parentViewportOverride = null;
    } else {
      this.parentViewportOverride = {
        top: isFinite(top) ? top : 0,
        left: isFinite(left) ? left : 0,
        width,
        height,
      };
    }
    this.onViewportOrDomChange();
  };

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly ngZone: NgZone
  ) {}

  /**
   * Ask the embedding parent to post {@link EMBED_PARENT_VIEWPORT_TYPE} (same target as height
   * reporting). Debounced so open dialogs do not spam.
   */
  private postRequestParentViewportPing(): void {
    const po = environment.iframeParentOrigin;
    const target =
      po !== undefined && po !== null && String(po).length > 0 ? String(po) : '*';
    try {
      window.parent.postMessage(
        { type: IFRAME_EMBED_REQUEST_PARENT_VIEWPORT_TYPE },
        target
      );
    } catch {
      /* ignore */
    }
    this.lastParentViewportPingAt = Date.now();
  }

  private maybeRequestParentViewportWhileDialogOpen(): void {
    if (this.parentViewportOverride) {
      return;
    }
    const now = Date.now();
    if (now - this.lastParentViewportPingAt < 500) {
      return;
    }
    this.postRequestParentViewportPing();
  }

  private readonly onViewportOrDomChange = (): void => {
    if (this.rafId !== undefined) {
      cancelAnimationFrame(this.rafId);
    }
    this.rafId = requestAnimationFrame(() => {
      this.rafId = undefined;
      this.patchViewportCssVars();
      this.patchMatDialogOverlayPanes();
    });
  };

  start(): void {
    if (this.started) {
      return;
    }
    if (!isEmbeddedFromParentOrigin()) {
      return;
    }

    this.started = true;
    this.document.documentElement.classList.add(
      EmbeddedParentViewportService.EMBEDDED_CLASS
    );

    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('message', this.onParentViewportMessage, true);
      this.document.addEventListener('click', this.onDocumentClick, true);
      this.patchViewportCssVars();
      this.patchMatDialogOverlayPanes();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.postRequestParentViewportPing());
      });
      const vv = window.visualViewport;
      if (vv) {
        vv.addEventListener('resize', this.onViewportOrDomChange);
        vv.addEventListener('scroll', this.onViewportOrDomChange);
      }
      window.addEventListener('resize', this.onViewportOrDomChange);
      this.mutationObserver = new MutationObserver((records) => {
        if (!this.overlayDomMayHaveChanged(records)) {
          return;
        }
        this.onViewportOrDomChange();
      });
      this.mutationObserver.observe(this.document.body, {
        childList: true,
        subtree: true,
      });
    });
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    const vv = window.visualViewport;
    if (vv) {
      vv.removeEventListener('resize', this.onViewportOrDomChange);
      vv.removeEventListener('scroll', this.onViewportOrDomChange);
    }
    window.removeEventListener('resize', this.onViewportOrDomChange);
    window.removeEventListener('message', this.onParentViewportMessage, true);
    this.document.removeEventListener('click', this.onDocumentClick, true);
    this.parentViewportOverride = null;
    this.lastClickedButton = null;

    this.mutationObserver?.disconnect();
    this.mutationObserver = undefined;

    if (this.rafId !== undefined) {
      cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }

    this.clearPatchedMatDialogOverlays();

    this.document.documentElement.classList.remove(
      EmbeddedParentViewportService.EMBEDDED_CLASS
    );
    for (const name of EmbeddedParentViewportService.CSS_VARS) {
      this.document.documentElement.style.removeProperty(name);
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private overlayDomMayHaveChanged(records: MutationRecord[]): boolean {
    for (const rec of records) {
      for (let i = 0; i < rec.addedNodes.length; i++) {
        const node = rec.addedNodes[i];
        if (!(node instanceof HTMLElement)) {
          continue;
        }
        if (
          node.classList.contains('cdk-overlay-container') ||
          node.classList.contains('cdk-overlay-pane') ||
          node.classList.contains('cdk-global-overlay-wrapper') ||
          node.querySelector?.(
            `.cdk-overlay-pane, .cdk-overlay-container, ${EmbeddedParentViewportService.MAT_DIALOG_CONTAINER_SELECTOR}`
          )
        ) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * When the parent posts {@link EMBED_PARENT_VIEWPORT_TYPE}, use that band (intersection of
   * parent viewport with the iframe). Otherwise fall back to this frame's visualViewport, which
   * matches the full iframe when the iframe is expanded to document height.
   */
  private getEffectiveEmbeddedViewport(): {
    top: number;
    left: number;
    width: number;
    height: number;
  } {
    const o = this.parentViewportOverride;
    if (o && o.width > 0 && o.height > 0) {
      return { top: o.top, left: o.left, width: o.width, height: o.height };
    }
    const vv = window.visualViewport;
    if (vv) {
      return {
        top: vv.offsetTop,
        left: vv.offsetLeft,
        width: vv.width,
        height: vv.height,
      };
    }
    return {
      top: 0,
      left: 0,
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }

  private patchViewportCssVars(): void {
    const root = this.document.documentElement;
    const vp = this.getEffectiveEmbeddedViewport();
    root.style.setProperty('--embedded-vv-top', `${vp.top}px`);
    root.style.setProperty('--embedded-vv-left', `${vp.left}px`);
    root.style.setProperty('--embedded-vv-width', `${vp.width}px`);
    root.style.setProperty('--embedded-vv-height', `${vp.height}px`);
  }

  private static readonly PATCHED_STYLE_PROPS = [
    'position',
    'top',
    'bottom',
    'left',
    'transform',
    'max-height',
    'max-width',
    'overflow-y',
  ] as const;

  /**
   * Positions Material 21 dialog panes in the parent-visible band when available (centered).
   * Without that band, `visualViewport` often matches the full (tall) iframe, so we fall
   * back to bottom-aligning just above the button that opened the dialog (or the bottom of
   * the effective viewport if no button). `position: fixed` is scoped to this iframe's
   * viewport (it does not scroll internally), so button anchoring keeps the dialog near
   * the trigger regardless of parent page scroll.
   */
  private patchMatDialogOverlayPanes(): void {
    if (!this.started) {
      return;
    }

    const hasParentBand = !!this.parentViewportOverride;
    const vp = this.getEffectiveEmbeddedViewport();
    const gap = EmbeddedParentViewportService.BOTTOM_GAP_PX;
    const iframeH = window.innerHeight;

    let hasMatDialogPane = false;

    this.document.querySelectorAll('.cdk-overlay-pane').forEach((el) => {
      const pane = el as HTMLElement;
      const isMatDialog = !!pane.querySelector(
        EmbeddedParentViewportService.MAT_DIALOG_CONTAINER_SELECTOR
      );

      if (!isMatDialog) {
        if (pane.getAttribute(EmbeddedParentViewportService.DIALOG_PATCH_ATTR) === '1') {
          pane.removeAttribute(EmbeddedParentViewportService.DIALOG_PATCH_ATTR);
          EmbeddedParentViewportService.PATCHED_STYLE_PROPS.forEach((p) =>
            pane.style.removeProperty(p)
          );
        }
        return;
      }

      pane.setAttribute(EmbeddedParentViewportService.DIALOG_PATCH_ATTR, '1');
      EmbeddedParentViewportService.PATCHED_STYLE_PROPS.forEach((p) =>
        pane.style.removeProperty(p)
      );
      pane.style.setProperty('position', 'fixed', 'important');

      if (hasParentBand) {
        // Parent posted a visible band — keep dialog centered in that band.
        const centerTop = vp.top + vp.height / 2;
        const centerLeft = vp.left + vp.width / 2;
        const maxH = Math.max(0, vp.height - 32);

        pane.style.setProperty('top', `${centerTop}px`, 'important');
        pane.style.setProperty('left', `${centerLeft}px`, 'important');
        pane.style.setProperty('transform', 'translate(-50%, -50%)', 'important');
        pane.style.setProperty('max-height', `${maxH}px`, 'important');
      } else {
        // No parent band yet — bottom fallback (prefer above the opening button).
        const anchorBtn = this.lastClickedButton;
        const canAnchorToButton =
          !!anchorBtn && this.document.contains(anchorBtn);

        let dialogBottomY: number;
        let centerX: number;
        let maxH: number;

        if (canAnchorToButton) {
          const btnRect = anchorBtn!.getBoundingClientRect();
          dialogBottomY = btnRect.top - gap;
          centerX = btnRect.left + btnRect.width / 2;
          maxH = Math.max(120, dialogBottomY - 16);
        } else {
          dialogBottomY = vp.top + vp.height - gap;
          centerX = vp.left + vp.width / 2;
          maxH = Math.max(120, vp.height - gap * 2);
        }

        let bottomOffset = iframeH - dialogBottomY;
        bottomOffset = Math.max(16, Math.min(bottomOffset, iframeH - 100));

        pane.style.setProperty('bottom', `${bottomOffset}px`, 'important');
        pane.style.setProperty('left', `${centerX}px`, 'important');
        pane.style.setProperty('transform', 'translateX(-50%)', 'important');
        pane.style.setProperty('max-height', `${maxH}px`, 'important');
        pane.style.setProperty('max-width', '90vw', 'important');
        pane.style.setProperty('overflow-y', 'auto', 'important');
      }

      hasMatDialogPane = true;
    });

    if (hasMatDialogPane) {
      this.maybeRequestParentViewportWhileDialogOpen();
    }
  }

  private clearPatchedMatDialogOverlays(): void {
    this.document
      .querySelectorAll(
        `[${EmbeddedParentViewportService.DIALOG_PATCH_ATTR}="1"]`
      )
      .forEach((el) => {
        const pane = el as HTMLElement;
        pane.removeAttribute(EmbeddedParentViewportService.DIALOG_PATCH_ATTR);
        EmbeddedParentViewportService.PATCHED_STYLE_PROPS.forEach((p) =>
          pane.style.removeProperty(p)
        );
      });
  }
}
